import React, { useState } from "react";

import { Box, FormLabel, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CheckboxGroupInput,
  Create,
  email,
  FormDataConsumer,
  maxLength,
  minLength,
  RadioButtonGroupInput,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
} from "react-admin";
import { useForm } from "react-final-form";
import setFieldData from "final-form-set-field-data";

import { AddressInput } from "../../../components/common/AddressInput";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";

import { getUserTypeAcls } from "../../../utils/appUtils";
import { REGEX_PHONE, UserType } from "../../../utils/constants";

import { UserCreateHelp } from "./UserHelp";
import { ValidateSaveButton } from "./ValidateSaveButton";
import {
  getUserTypeDescription,
  getUserTypeOptions,
  validateEmailUnicity,
} from "./userUtils";

const useStyles = makeStyles((theme) => ({
  radioButton: {
    marginTop: theme.spacing(0),
    marginLeft: theme.spacing(1),
  },
  userTypeLabelRow: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  userTypeLabel: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.2,
  },
  hiddenRadioLabel: {
    display: "none",
  },
}));

const validateRequired = required();
const validateEmail = () => (value, allValues, meta) => {
  // meta set on saveValidate and onBlur via mutators.setFieldData
  const metaError = meta?.data?.error;
  if (metaError) return metaError;

  const validators = [required(), email()];
  for (const v of validators) {
    const err = v(value, allValues, meta);
    if (err) return err;
  }
  return undefined;
};
const validatePhone = [
  required(),
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const UserCreateForm = (props) => {
  const classes = useStyles();
  const form = useForm();
  const [aclList, setAclList] = useState(getUserTypeAcls(UserType.CREW));

  const handleUserTypeChange = (value) => {
    const newAclList = getUserTypeAcls(value);
    setAclList(newAclList);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Grid container direction="column" spacing={1}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="firstName"
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="lastName"
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    validate={validateRequired}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    validate={validateEmail()}
                    onBlur={async () => {
                      await validateEmailUnicity(form);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    variant="outlined"
                    fullWidth
                    validate={validatePhone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    gutterBottom
                    variant="overline"
                    color="textSecondary"
                  >
                    Home Address
                  </Typography>
                  <AddressInput
                    source="address"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v : undefined)}
                    allValues={formData}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={1}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                  <Box className={classes.userTypeLabelRow}>
                    <FormLabel
                      component="span"
                      className={classes.userTypeLabel}
                    >
                      User Type *
                    </FormLabel>
                    <SmartTipIcon label="user-type" inline ml={1} />
                  </Box>
                  <Box display="flex" fullWidth>
                    <RadioButtonGroupInput
                      source="type"
                      label={false}
                      row={false}
                      className={classes.radioButton}
                      classes={{ label: classes.hiddenRadioLabel }}
                      choices={getUserTypeOptions(null)}
                      validate={validateRequired}
                      defaultValue={UserType.CREW}
                      onChange={handleUserTypeChange}
                      helperText={getUserTypeDescription(formData.type)}
                    />
                  </Box>
                </Grid>
                {formData.type && formData.type !== UserType.MANAGER && (
                  <Grid item xs={12}>
                    <CheckboxGroupInput
                      source="acl"
                      label="Access Permissions"
                      row={false}
                      fullWidth
                      choices={aclList}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const UserCreateActions = (props) => (
  <TopToolbar>
    <HelpButton>
      <UserCreateHelp {...props} />
    </HelpButton>
    <BackButton {...props} />
  </TopToolbar>
);

const UserCreateToolbar = (props) => (
  <Toolbar {...props}>
    <ValidateSaveButton />
  </Toolbar>
);

export const UserCreate = (props) => {
  return (
    <Create
      {...props}
      title={<ForceTitle title="New User" />}
      actions={<UserCreateActions />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        mutators={{ setFieldData }}
        toolbar={<UserCreateToolbar />}
      >
        <UserCreateForm />
      </SimpleForm>
    </Create>
  );
};
